import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Platform, StyleSheet, View } from 'react-native';

const warehouseNavigatorLogo = require('../../../../assets/warehouse_navigator.png');

export type ReviewProgressTaskPrintItem = {
	id: string;
	title: string;
	subHeader?: string;
	description?: string;
	status?: string;
	category?: string;
};

export interface ReviewProgressTasksPrintRef {
	print: () => void;
	getHtml?: () => string;
}

const escapeHtml = (text: string): string => {
	const div = typeof document !== 'undefined' ? document.createElement('div') : null;
	if (div) {
		div.textContent = String(text ?? '');
		return div.innerHTML;
	}

	return String(text ?? '').replace(/[&<>"']/g, (m) =>
		(
			{
				'&': '&amp;',
				'<': '&lt;',
				'>': '&gt;',
				'"': '&quot;',
				"'": '&#39;',
			} as const
		)[m] || m,
	);
};

const renderStyles = (): string => {
	return `
		@page { size: landscape; margin: 0.5in 0.25in; }

		html, body { padding: 0; margin: 0; }
		body {
			font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
			color: #1C1C1C;
			background: #FFFFFF;
		}

		* { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }

		table.report-table { width: 100%; border-collapse: collapse; }
		thead { display: table-header-group; }
		tfoot { display: table-footer-group; }
		tr { page-break-inside: avoid; break-inside: avoid; }

		.print-header-cell {
			padding: 0 0 10px 0;
			border-bottom: 0;
			text-align: left;
			font-weight: normal;
		}

		.header-bar {
			display: flex;
			align-items: center;
			justify-content: space-between;
			border: 1px solid #DADADA;
			border-radius: 6px;
			padding: 8px 12px;
			background: #FFFFFF;
		}
		.header-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
		.logo { width: 20px; height: 20px; object-fit: contain; border-radius: 4px; flex-shrink: 0; }
		.header-title { font-weight: 700; font-size: 16px; color: #093F6E; white-space: nowrap; }
		.header-date { font-size: 13px; font-weight: 400; color: #6B6B6B; white-space: nowrap; }

		.section-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 10px 0 12px;
		}
		.section-title { font-weight: 700; font-size: 14px; letter-spacing: 0.2px; }

		tr.cards-row { page-break-inside: avoid; break-inside: avoid; }
		td.cards-cell { padding: 12px 0 0; }

		.task-row {
			display: flex;
			gap: 12px;
			width: 100%;
			page-break-inside: avoid;
			break-inside: avoid;
		}

		.task-card {
			flex: 1;
			background: #FFFFFF;
			border-radius: 12px;
			border: 1px solid #E0E7FF;
			padding: 16px;
			page-break-inside: avoid;
			break-inside: avoid;
		}
		.task-card.placeholder { visibility: hidden; }

		.task-title { font-size: 16px; font-weight: 600; color: #1C1C1C; margin-bottom: 6px; }
		.task-subheader { font-size: 12px; font-weight: 500; color: #6B6B6B; margin-bottom: 8px; }
		.task-description { font-size: 12px; font-weight: 400; color: #333333; line-height: 16px; margin-bottom: 8px; }
		.task-meta { display: flex; gap: 8px; font-size: 11px; color: #5A6B7A; }
		.task-chip {
			border: 1px solid #D8E7FB;
			padding: 2px 6px;
			border-radius: 10px;
			background: #F5F9FF;
		}
	`;
};

const renderPrintHeader = (opts: { date: string; logoBase64?: string }): string => {
	const logoImg = opts.logoBase64
		? `<img class="logo" src="${opts.logoBase64}" alt="Warehouse Navigator" />`
		: `<div class="logo" style="background: transparent;"></div>`;

	return `
		<tr>
			<th class="print-header-cell">
				<div class="header-bar">
					<div class="header-left">
						${logoImg}
						<span class="header-title">Warehouse Navigator</span>
					</div>
					<span class="header-date">${escapeHtml(opts.date)}</span>
				</div>
			</th>
		</tr>
	`;
};

const renderSectionHeader = (title: string): string => {
	return `
		<tr>
			<th style="padding:0; text-align:left; font-weight: normal;">
				<div class="section-row">
					<span class="section-title">${escapeHtml(title)}</span>
				</div>
			</th>
		</tr>
	`;
};

const renderTaskCard = (task: ReviewProgressTaskPrintItem): string => {
	const subHeader = task.subHeader ? `<div class="task-subheader">${escapeHtml(task.subHeader)}</div>` : '';
	const description = task.description ? `<div class="task-description">${escapeHtml(task.description)}</div>` : '';
	const status = task.status ? `<span class="task-chip">${escapeHtml(task.status)}</span>` : '';
	const category = task.category ? `<span class="task-chip">${escapeHtml(task.category)}</span>` : '';

	return `
		<div class="task-card">
			<div class="task-title">${escapeHtml(task.title || '')}</div>
			${subHeader}
			${description}
			<div class="task-meta">${status}${category}</div>
		</div>
	`;
};

const renderTaskGrid = (tasks: ReviewProgressTaskPrintItem[], perRow: number): string => {
	const rows: ReviewProgressTaskPrintItem[][] = [];
	for (let i = 0; i < tasks.length; i += perRow) {
		rows.push(tasks.slice(i, i + perRow));
	}

	const rowHtml = rows
		.map((row) => {
			const placeholdersNeeded = Math.max(0, perRow - row.length);
			const cardsHtml = row.map(renderTaskCard).join('');
			const placeholders = Array.from({ length: placeholdersNeeded })
				.map(() => `<div class="task-card placeholder"></div>`)
				.join('');

			return `
				<tr class="cards-row">
					<td class="cards-cell">
						<div class="task-row">
							${cardsHtml}
							${placeholders}
						</div>
					</td>
				</tr>
			`;
		})
		.join('');

	return `<tbody>${rowHtml}</tbody>`;
};

export const generateReviewProgressTasksPrintHTML = (args: {
	date: string;
	logoBase64?: string;
	tasks: ReviewProgressTaskPrintItem[];
}): string => {
	const headerHtml = `
		<thead>
			${renderPrintHeader({ date: args.date, logoBase64: args.logoBase64 })}
			${renderSectionHeader('Selected Tasks')}
		</thead>
	`;

	const gridHtml = renderTaskGrid(args.tasks || [], 3);

	return `<!DOCTYPE html>
<html lang="en">
	<head>
		<meta charset="UTF-8" />
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		<title>Selected Tasks Print</title>
		<style>
			${renderStyles()}
		</style>
	</head>
	<body>
		<table class="report-table">
			${headerHtml}
			${gridHtml}
		</table>
	</body>
</html>`;
};

type ReviewProgressTasksPrintProps = {
	tasks: ReviewProgressTaskPrintItem[];
};

const ReviewProgressTasksPrint = forwardRef(function ReviewProgressTasksPrint(
	props: ReviewProgressTasksPrintProps,
	ref: React.Ref<ReviewProgressTasksPrintRef>,
) {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const [logoBase64, setLogoBase64] = useState<string>('');

	useEffect(() => {
		const loadLogo = async () => {
			if (Platform.OS !== 'web') return;
			try {
				const response = await fetch(warehouseNavigatorLogo);
				const blob = await response.blob();
				const reader = new FileReader();
				reader.onloadend = () => setLogoBase64(reader.result as string);
				reader.readAsDataURL(blob);
			} catch (e) {
				console.warn('Failed to load logo for print:', e);
				setLogoBase64('');
			}
		};
		loadLogo();
	}, []);

	const printDate = useMemo(
		() =>
			new Date().toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
		[],
	);

	const htmlContent = useMemo(
		() =>
			generateReviewProgressTasksPrintHTML({
				date: printDate,
				logoBase64,
				tasks: props.tasks || [],
			}),
		[printDate, logoBase64, props.tasks],
	);

	useImperativeHandle(
		ref,
		() => ({
			print: () => {
				if (Platform.OS === 'web') {
					const iframe = iframeRef.current;
					if (iframe?.contentWindow) {
						iframe.contentWindow.focus();
						iframe.contentWindow.print();
					}
					return;
				}

				console.log('Mobile print - use react-native-print with HTML:', htmlContent);
			},
			getHtml: () => htmlContent,
		}),
		[htmlContent],
	);

	useEffect(() => {
		if (Platform.OS === 'web' && iframeRef.current?.contentDocument) {
			const doc = iframeRef.current.contentDocument;
			doc.open();
			doc.write(htmlContent);
			doc.close();
		}
	}, [htmlContent]);

	if (Platform.OS !== 'web') {
		return <View style={styles.hiddenContainer} />;
	}

	return (
		<View style={styles.hiddenContainer}>
			{/* @ts-ignore - iframe is web-only */}
			<iframe
				ref={iframeRef}
				style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					opacity: 0,
					pointerEvents: 'none',
				}}
				title="Selected Tasks Print"
			/>
		</View>
	);
});

const styles = StyleSheet.create({
	hiddenContainer: {
		position: 'absolute',
		left: -9999,
		top: -9999,
		width: 1,
		height: 1,
		opacity: 0,
	},
});

ReviewProgressTasksPrint.displayName = 'ReviewProgressTasksPrint';
(ReviewProgressTasksPrint as any).PureComponent = true;

export default ReviewProgressTasksPrint;
